import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextareaAutosize,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Divider,
  Stack,
  Badge,
  Fade,
  Grow,
  Slide,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CheckCircle,
  Cancel,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AccessTime,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Star,
  StarBorder,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEventManagement } from '../hooks/useEventManagement';
import { format } from 'date-fns';

const EventManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    events,
    loading,
    error,
    pagination,
    filters,
    stats,
    fetchEvents,
    approveEvent,
    rejectEvent,
    updateEventStatus,
    toggleFeatured,
    deleteEvent,
    changePage,
    updateFilters,
    clearFilters,
    resetToFirstPage,
    clearError
  } = useEventManagement();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [pricingTypeFilter, setPricingTypeFilter] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', event: null });
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Event categories and types
  const eventCategories = [
    'Entertainment', 'Sports', 'Education', 'Business', 'Technology',
    'Arts', 'Food', 'Music', 'Health & Wellness', 'Fashion', 'Travel', 'Other'
  ];

  const eventStatuses = [
    'draft', 'pending', 'approved', 'rejected', 'active', 'completed', 'cancelled'
  ];

  const eventTypes = ['in-person', 'virtual', 'hybrid'];
  const pricingTypes = ['free', 'paid'];

  // Get pricing type color
  const getPricingTypeColor = (pricingType) => {
    switch (pricingType) {
      case 'free': return { color: 'success', bgColor: alpha(theme.palette.success.main, 0.1) };
      case 'paid': return { color: 'warning', bgColor: alpha(theme.palette.warning.main, 0.1) };
      default: return { color: 'default', bgColor: alpha(theme.palette.grey[500], 0.1) };
    }
  };

  // Handle search
  const handleSearch = () => {
    updateFilters({
      search: searchTerm,
      status: statusFilter,
      category: categoryFilter,
      eventType: eventTypeFilter,
      pricingType: pricingTypeFilter
    });
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCategoryFilter('');
    setEventTypeFilter('');
    setPricingTypeFilter('');
    clearFilters();
  };

  // Handle action dialog
  const handleActionDialog = (type, event) => {
    setActionDialog({ open: true, type, event });
    setActionReason('');
  };

  // Handle action confirmation
  const handleActionConfirm = async () => {
    const { type, event } = actionDialog;
    setActionLoading(true);

    try {
      let result;
      switch (type) {
        case 'approve':
          result = await approveEvent(event.id, { reason: actionReason });
          break;
        case 'reject':
          result = await rejectEvent(event.id, { reason: actionReason });
          break;
        case 'activate':
          result = await updateEventStatus(event.id, 'active', actionReason);
          break;
        case 'complete':
          result = await updateEventStatus(event.id, 'completed', actionReason);
          break;
        case 'status':
          result = await updateEventStatus(event.id, actionDialog.newStatus, actionReason);
          break;
        case 'delete':
          result = await deleteEvent(event.id);
          break;
        default:
          break;
      }

      if (result?.success) {
        setActionDialog({ open: false, type: '', event: null });
        setActionReason('');
        // Refresh events after action
        fetchEvents(pagination.page);
        // Show success message (you can add a toast notification here)
        console.log(`${type} action completed successfully`);
      }
    } catch (error) {
      console.error('Action failed:', error);
      // Show error message (you can add a toast notification here)
    } finally {
      setActionLoading(false);
    }
  };

  // Get status color and styling
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return { color: 'default', bgColor: alpha(theme.palette.grey[500], 0.1) };
      case 'pending': return { color: 'warning', bgColor: alpha(theme.palette.warning.main, 0.1) };
      case 'approved': return { color: 'success', bgColor: alpha(theme.palette.success.main, 0.1) };
      case 'rejected': return { color: 'error', bgColor: alpha(theme.palette.error.main, 0.1) };
      case 'active': return { color: 'info', bgColor: alpha(theme.palette.info.main, 0.1) };
      case 'completed': return { color: 'default', bgColor: alpha(theme.palette.grey[600], 0.1) };
      case 'cancelled': return { color: 'error', bgColor: alpha(theme.palette.error.main, 0.1) };
      default: return { color: 'default', bgColor: alpha(theme.palette.grey[500], 0.1) };
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  // Format time
  const formatTime = (date) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'HH:mm');
    } catch {
      return 'Invalid Time';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <EditIcon fontSize="small" />;
      case 'pending': return <ScheduleIcon fontSize="small" />;
      case 'approved': return <CheckCircle fontSize="small" />;
      case 'rejected': return <Cancel fontSize="small" />;
      case 'active': return <TrendingUpIcon fontSize="small" />;
      case 'completed': return <Star fontSize="small" />;
      case 'cancelled': return <Cancel fontSize="small" />;
      default: return <EventIcon fontSize="small" />;
    }
  };

  // Get available status options for an event
  const getAvailableStatusOptions = (currentStatus) => {
    switch (currentStatus) {
      case 'draft':
        return ['pending', 'approved', 'rejected'];
      case 'pending':
        return ['approved', 'rejected'];
      case 'approved':
        return ['active', 'rejected'];
      case 'active':
        return ['completed', 'cancelled'];
      case 'rejected':
        return ['approved'];
      case 'completed':
        return ['active'];
      case 'cancelled':
        return ['active'];
      default:
        return ['pending', 'approved', 'rejected'];
    }
  };

  // Handle status change
  const handleStatusChange = (event, newStatus) => {
    setActionDialog({
      open: true,
      type: 'status',
      event: event,
      newStatus: newStatus
    });
  };

  // Handle featured toggle
  const handleToggleFeatured = async (event) => {
    try {
      setActionLoading(true);
      const newFeaturedStatus = !event.isFeatured;
      const result = await toggleFeatured(event.id, newFeaturedStatus);

      if (result.success) {
        // Success message will be shown via toast if available
        // The event state will be updated automatically by the hook
      } else {
        // Handle error - you might want to show a toast here
        console.error('Failed to toggle featured status:', result.message);
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header with gradient background */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          boxShadow: theme.shadows[4]
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 0.5 }}>
          🎯 Event Management
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 300 }}>
          Manage and approve events from vendors with powerful tools and insights
        </Typography>
      </Box>

      {/* Statistics Cards with enhanced styling */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              boxShadow: theme.shadows[2],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <EventIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.total || 0}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Events</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
              color: 'white',
              boxShadow: theme.shadows[4],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <ScheduleIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.pending || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Pending Approval</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              color: 'white',
              boxShadow: theme.shadows[4],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <CheckCircle sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.approved || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Approved</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
              color: 'white',
              boxShadow: theme.shadows[4],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <Cancel sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.rejected || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Rejected</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
              color: 'white',
              boxShadow: theme.shadows[4],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <TrendingUpIcon sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.active || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Active</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.grey[600]} 0%, ${theme.palette.grey[800]} 100%)`,
              color: 'white',
              boxShadow: theme.shadows[4],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8]
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <Star sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.completed || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Completed</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters with enhanced styling */}
      <Card sx={{ mb: 2, boxShadow: theme.shadows[1], borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
            🔍 Search & Filter Events
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                label="Search Events"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  sx: { borderRadius: 2 }
                }}
                placeholder="Search by title, vendor..."
                variant="outlined"
                size="medium"
              />
            </Grid>
            <Grid item xs={12} md={1.8}>
              <FormControl fullWidth size="medium">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {eventStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.8}>
              <FormControl fullWidth size="medium">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {eventCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.8}>
              <FormControl fullWidth size="medium">
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={eventTypeFilter}
                  label="Event Type"
                  onChange={(e) => setEventTypeFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {eventTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.8}>
              <FormControl fullWidth size="medium">
                <InputLabel>Pricing Type</InputLabel>
                <Select
                  value={pricingTypeFilter}
                  label="Pricing Type"
                  onChange={(e) => setPricingTypeFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Pricing</MenuItem>
                  {pricingTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={type.charAt(0).toUpperCase() + type.slice(1)}
                          size="small"
                          color={getPricingTypeColor(type).color}
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.3}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<FilterIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    minWidth: 'auto',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: theme.shadows[2],
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Filter
                </Button>
                <Tooltip title="Clear all filters">
                  <IconButton
                    onClick={handleClearFilters}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        borderColor: theme.palette.error.main
                      }
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh events">
                  <IconButton
                    onClick={() => fetchEvents(1)}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderColor: theme.palette.primary.main,
                        transform: 'rotate(180deg)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Fade in={!!error}>
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={clearError}
            action={
              <Button color="inherit" size="small" onClick={clearError}>
                Dismiss
              </Button>
            }
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Events Table with enhanced styling */}
      <Card sx={{ boxShadow: theme.shadows[1], borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, maxHeight: 500 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  '& th': {
                    fontWeight: 600,
                    color: 'text.primary',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    fontSize: '0.875rem',
                    py: 1
                  }
                }}>
                  <TableCell>Event</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Pricing</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tickets</TableCell>
                  <TableCell align="center">Featured</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1" color="text.secondary">
                          Loading events...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <EventIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary">
                          No events found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your search criteria or filters
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((event, index) => (
                    <Grow in={true} timeout={index * 100} key={event.id}>
                      <TableRow
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                width: 36,
                                height: 36
                              }}
                            >
                              <EventIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                <Typography variant="body1" fontWeight="bold">
                                  {event.title}
                                </Typography>
                                {event.isFeatured && (
                                  <Chip
                                    icon={<Star sx={{ fontSize: 14 }} />}
                                    label="Featured"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                      color: theme.palette.warning.dark,
                                      fontWeight: 600,
                                      '& .MuiChip-icon': {
                                        color: theme.palette.warning.main
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200 }}>
                                {event.description?.substring(0, 50)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                color: theme.palette.secondary.main,
                                width: 28,
                                height: 28
                              }}
                            >
                              <BusinessIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Typography variant="caption" fontWeight="medium">
                              {event.vendorId.businessName || 'Unknown Vendor'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.category}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 1.5,
                              fontWeight: 500,
                              borderColor: alpha(theme.palette.primary.main, 0.3),
                              height: 24,
                              fontSize: '0.7rem'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={event.pricingType ? (event.pricingType.charAt(0).toUpperCase() + event.pricingType.slice(1)) : 'Paid'}
                            size="small"
                            color={getPricingTypeColor(event.pricingType || 'paid').color}
                            sx={{
                              borderRadius: 1.5,
                              fontWeight: 600,
                              bgcolor: getPricingTypeColor(event.pricingType || 'paid').bgColor,
                              height: 24,
                              fontSize: '0.7rem',
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="caption" fontWeight="medium" sx={{ mb: 0.25 }}>
                              {formatDate(event.startDate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime fontSize="small" />
                              {formatTime(event.startDate)} - {formatTime(event.endDate)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon color="action" fontSize="small" />
                            <Typography variant="caption">
                              {event.venue?.city || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={event.status}
                              color={getStatusColor(event.status).color}
                              size="small"
                              icon={getStatusIcon(event.status)}
                              sx={{
                                borderRadius: 1.5,
                                fontWeight: 500,
                                bgcolor: getStatusColor(event.status).bgColor,
                                height: 24,
                                fontSize: '0.7rem'
                              }}
                            />
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value=""
                                onChange={(e) => handleStatusChange(event, e.target.value)}
                                displayEmpty
                                sx={{
                                  height: 28,
                                  fontSize: '0.7rem',
                                  '& .MuiSelect-select': { py: 0.25 }
                                }}
                              >
                                <MenuItem value="" disabled>
                                  <Typography variant="caption" color="text.secondary">
                                    Change Status
                                  </Typography>
                                </MenuItem>
                                {getAvailableStatusOptions(event.status).map((status) => (
                                  <MenuItem key={status} value={status}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {getStatusIcon(status)}
                                      <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                        {status}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ fontSize: '0.85rem' }}>
                              {event.soldTickets || 0}/{event.totalTickets || 0}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: '0.65rem',
                                  display: 'block'
                                }}
                              >
                                {event.totalTickets > 0
                                  ? `${Math.round(((event.soldTickets || 0) / event.totalTickets) * 100)}% sold`
                                  : '0% sold'
                                }
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={event.isFeatured ? "Remove from featured" : "Mark as featured"} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleFeatured(event)}
                              disabled={actionLoading}
                              sx={{
                                color: event.isFeatured ? theme.palette.warning.main : 'text.secondary',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                                  color: theme.palette.warning.main
                                }
                              }}
                            >
                              {event.isFeatured ? <Star /> : <StarBorder />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {event.status === 'pending' && (
                              <>
                                <Tooltip title="Approve Event" arrow>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleActionDialog('approve', event)}
                                    sx={{
                                      bgcolor: alpha(theme.palette.success.main, 0.1),
                                      '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) }
                                    }}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject Event" arrow>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleActionDialog('reject', event)}
                                    sx={{
                                      bgcolor: alpha(theme.palette.error.main, 0.1),
                                      '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                                    }}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="View Details" arrow>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setSelectedEvent(event)}
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {event.status === 'approved' && (
                              <Tooltip title="Activate Event" arrow>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleActionDialog('activate', event)}
                                  sx={{
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                                  }}
                                >
                                  <TrendingUpIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {event.status === 'active' && (
                              <Tooltip title="Complete Event" arrow>
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={() => handleActionDialog('complete', event)}
                                  sx={{
                                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.2) }
                                  }}
                                >
                                  <Star />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Manage Event" arrow>
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => navigate(`/dashboard/event-management/${event.id}`)}
                                sx={{
                                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) }
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Details" arrow>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setSelectedEvent(event)}
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Event" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleActionDialog('delete', event)}
                                sx={{
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Grow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination with enhanced styling */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: 'grey.50' }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, page) => changePage(page)}
                color="primary"
                showFirstButton
                showLastButton
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          )}

          {/* Results Info */}
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} events
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Enhanced Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, type: '', event: null })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: theme.shadows[8] }
        }}
      >
        <DialogTitle sx={{
          bgcolor: actionDialog.type === 'approve' ? 'success.main' :
            actionDialog.type === 'reject' ? 'error.main' :
              actionDialog.type === 'activate' ? 'info.main' :
                actionDialog.type === 'complete' ? 'default.main' :
                  actionDialog.type === 'status' ? 'primary.main' :
                    'error.main',
          color: 'white',
          textAlign: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            {actionDialog.type === 'approve' && <CheckCircle />}
            {actionDialog.type === 'reject' && <Cancel />}
            {actionDialog.type === 'activate' && <TrendingUpIcon />}
            {actionDialog.type === 'complete' && <Star />}
            {actionDialog.type === 'status' && <EditIcon />}
            {actionDialog.type === 'delete' && <DeleteIcon />}
            {actionDialog.type === 'approve' && 'Approve Event'}
            {actionDialog.type === 'reject' && 'Reject Event'}
            {actionDialog.type === 'activate' && 'Activate Event'}
            {actionDialog.type === 'complete' && 'Complete Event'}
            {actionDialog.type === 'status' && `Change Status to ${actionDialog.newStatus}`}
            {actionDialog.type === 'delete' && 'Delete Event'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            {actionDialog.type === 'approve' && `Are you sure you want to approve "${actionDialog.event?.title}"?`}
            {actionDialog.type === 'reject' && `Are you sure you want to reject "${actionDialog.event?.title}"?`}
            {actionDialog.type === 'activate' && `Are you sure you want to activate "${actionDialog.event?.title}"?`}
            {actionDialog.type === 'complete' && `Are you sure you want to mark "${actionDialog.event?.title}" as completed?`}
            {actionDialog.type === 'status' && `Are you sure you want to change the status of "${actionDialog.event?.title}" from "${actionDialog.event?.status}" to "${actionDialog.newStatus}"?`}
            {actionDialog.type === 'delete' && `Are you sure you want to delete "${actionDialog.event?.title}"? This action cannot be undone.`}
          </Typography>

          {(actionDialog.type === 'approve' || actionDialog.type === 'reject' || actionDialog.type === 'activate' || actionDialog.type === 'complete' || actionDialog.type === 'status') && (
            <TextField
              fullWidth
              label="Reason (Optional)"
              multiline
              rows={3}
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              placeholder="Enter a reason for this action..."
              sx={{ mt: 2 }}
              variant="outlined"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setActionDialog({ open: false, type: '', event: null })}
            variant="outlined"
            disabled={actionLoading}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            disabled={actionLoading}
            color={actionDialog.type === 'approve' ? 'success' :
              actionDialog.type === 'reject' ? 'error' :
                actionDialog.type === 'activate' ? 'info' :
                  actionDialog.type === 'complete' ? 'default' :
                    actionDialog.type === 'status' ? 'primary' :
                      'error'}
            sx={{
              borderRadius: 2,
              px: 3,
              background: actionDialog.type === 'approve' ?
                `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)` :
                actionDialog.type === 'reject' ?
                  `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)` :
                  actionDialog.type === 'activate' ?
                    `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)` :
                    actionDialog.type === 'complete' ?
                      `linear-gradient(135deg, ${theme.palette.grey[600]} 0%, ${theme.palette.grey[800]} 100%)` :
                      actionDialog.type === 'status' ?
                        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` :
                        `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
              '&:disabled': {
                background: theme.palette.grey[400]
              }
            }}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {actionLoading ? 'Processing...' : (
              <>
                {actionDialog.type === 'approve' && 'Approve'}
                {actionDialog.type === 'reject' && 'Reject'}
                {actionDialog.type === 'activate' && 'Activate'}
                {actionDialog.type === 'complete' && 'Complete'}
                {actionDialog.type === 'delete' && 'Delete'}
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Event Details Dialog */}
      <Dialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: theme.shadows[8] }
        }}
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{
              bgcolor: 'primary.main',
              color: 'white',
              textAlign: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <EventIcon />
                Event Details
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    📋 Event Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedEvent.title}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{selectedEvent.description}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                    <Chip label={selectedEvent.category} size="small" color="primary" />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Event Type</Typography>
                    <Chip label={selectedEvent.eventType} size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Pricing Type</Typography>
                    <Chip
                      label={selectedEvent.pricingType ? (selectedEvent.pricingType.charAt(0).toUpperCase() + selectedEvent.pricingType.slice(1)) : 'Paid'}
                      size="small"
                      color={getPricingTypeColor(selectedEvent.pricingType || 'paid').color}
                      sx={{
                        fontWeight: 600,
                        bgcolor: getPricingTypeColor(selectedEvent.pricingType || 'paid').bgColor,
                        textTransform: 'capitalize'
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    📅 Event Details
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDate(selectedEvent.startDate)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">End Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDate(selectedEvent.endDate)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                    <Typography variant="body1">
                      {formatTime(selectedEvent.startDate)} - {formatTime(selectedEvent.endDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip
                      label={selectedEvent.status}
                      color={getStatusColor(selectedEvent.status).color}
                      size="small"
                      icon={getStatusIcon(selectedEvent.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    🏢 Vendor Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main' }}>
                      <BusinessIcon />
                    </Avatar>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedEvent.vendorId.businessName || 'Unknown Vendor'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    🎫 Ticket Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        <Typography variant="h3" color="primary.main" sx={{ fontWeight: 'bold' }}>
                          {selectedEvent.totalTickets || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Tickets</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 3,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }}>
                        <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                          {selectedEvent.soldTickets || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Sold Tickets</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
              <Button
                onClick={() => setSelectedEvent(null)}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default EventManagement; 